import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowLeft, Check, Heart } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp } from "@/lib/motion";

// TODO: Replace with the exact Google review short link (Google Business Profile → "Get more reviews").
const GOOGLE_REVIEW_URL =
  "https://www.google.com/maps/search/?api=1&query=%CE%86%CE%BD%CE%BD%CE%B1+%CE%A0%CE%B1%CF%80%CE%B1%CF%87%CF%81%CE%BF%CE%BD%CE%BF%CF%80%CE%BF%CF%8D%CE%BB%CE%BF%CF%85+%CE%A8%CF%85%CF%87%CE%BF%CE%BB%CF%8C%CE%B3%CE%BF%CF%82+%CE%A0%CE%AC%CF%84%CF%81%CE%B1";

export default function ThankYou() {
  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{"Ευχαριστώ · Άννα Παπαχρονόπουλου"}</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="description" content="Σελίδα ευχαριστιών για τους ασθενείς της Άννας Παπαχρονόπουλου." />
      </Helmet>

      <Header />

      <main
        className="bg-background pt-32 pb-24 min-h-screen"
        data-testid="thank-you-page"
      >
        <div className="max-w-[820px] mx-auto px-6 sm:px-12">
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-10"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>

            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary mb-7">
              <Heart size={22} strokeWidth={1.5} />
            </div>

            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[60px] leading-[1.05]">
              Ευχαριστώ που ήρθες <span className="italic font-light">μέχρι εδώ.</span>
            </h1>

            <p className="mt-7 font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed">
              Η εμπιστοσύνη σου σημαίνει πολλά. Αν η συνεδρία μας σε βοήθησε,
              θα χαρώ αν αφιερώσεις <strong className="text-foreground font-normal">30 δευτερόλεπτα</strong> για
              να γράψεις λίγα λόγια στο Google.
            </p>

            <p className="mt-4 font-body font-light text-base text-foreground/70 leading-relaxed">
              Ένα μικρό σχόλιο βοηθά άλλους ανθρώπους που διστάζουν να ξεκινήσουν
              τη δική τους θεραπευτική διαδρομή να βρουν τον δρόμο τους — και
              στηρίζει την προσπάθειά μου να συνεχίζω αυτή τη δουλειά.
            </p>
          </motion.div>

          {/* Stars */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-12 flex flex-col items-center text-center bg-card rounded-3xl p-10 sm:p-14 border border-border/50"
          >
            <div className="flex gap-2 text-primary mb-6">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={32}
                  strokeWidth={1.2}
                  fill="currentColor"
                  data-testid={`star-${s}`}
                />
              ))}
            </div>
            <p className="font-heading text-2xl sm:text-3xl text-foreground tracking-tight max-w-[420px]">
              Άσε ένα <span className="italic font-light">review στο Google</span>
            </p>
            <p className="mt-3 font-body font-light text-sm text-muted-foreground max-w-[380px]">
              Θα ανοίξει η σελίδα Google Maps. Δεν χρειάζεται λογαριασμός Gmail
              αν είσαι ήδη συνδεδεμένος/η.
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground text-background px-8 py-4 font-body text-base font-medium hover:-translate-y-0.5 hover:bg-foreground/85 transition-all duration-300"
            >
              <Star size={16} strokeWidth={1.5} fill="currentColor" />
              Άσε review στο Google
            </a>
          </motion.div>

          {/* Why it matters */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {[
              "Παίρνει 30 δευτερόλεπτα",
              "Είναι ανώνυμο αν θέλεις (μόνο όνομα)",
              "Βοηθάει πραγματικά κάποιον να ξεκινήσει",
            ].map((t) => (
              <div
                key={t}
                className="flex items-start gap-3 bg-background border border-border/50 rounded-2xl p-5"
              >
                <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-primary/15 flex items-center justify-center text-primary">
                  <Check size={11} strokeWidth={2} />
                </span>
                <p className="font-body text-sm text-foreground/80 leading-relaxed">
                  {t}
                </p>
              </div>
            ))}
          </motion.div>

          {/* Skip option */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.35 }}
            className="mt-14 text-center"
          >
            <Link
              to="/"
              data-testid="skip-link"
              className="text-sm font-body text-muted-foreground hover:text-foreground transition-colors"
            >
              Ίσως άλλη φορά →
            </Link>
            <p className="mt-3 font-body font-light text-xs text-muted-foreground italic max-w-[420px] mx-auto">
              Δεν αισθάνομαι καμία πίεση. Είμαι ευγνώμων για κάθε άνθρωπο που
              μου εμπιστεύεται την ιστορία του — με ή χωρίς review.
            </p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
